import { ApiCallLogEntry, snapshotApiCalls } from "@appretio/api";

/**
 * Client-side diagnostics attached to every report as `context.diagnostics`:
 * a rolling buffer of recent console errors/warnings and uncaught failures,
 * plus the API package's recent-calls log. Observe-only — the console and
 * error events behave exactly as before — and size-disciplined: stacks and
 * messages are clipped per entry, and the whole snapshot is truncated (never
 * rejected) to stay a small slice of the API's context budget.
 */

export interface ClientErrorEntry {
  kind:
    | "console.error"
    | "console.warn"
    | "window.onerror"
    | "unhandledrejection";
  message: string;
  /** `file:line:col` for window.onerror events, when the browser gave one. */
  source?: string;
  stack?: string;
  timestamp: string;
}

export interface DiagnosticsSnapshot {
  errors: ClientErrorEntry[];
  api_calls: ApiCallLogEntry[];
  /** Set when entries were dropped to fit the serialized budget. */
  truncated?: true;
}

/** Rolling-buffer size for captured errors/warnings. */
export const MAX_ERROR_ENTRIES = 20;
/** Per-entry clip for stacks and messages. */
export const MAX_STACK_CHARS = 500;
export const MAX_ERROR_MESSAGE_CHARS = 500;
/**
 * Serialized budget for the whole diagnostics block — a deliberate small
 * slice of the API's MAX_CONTEXT_CHARS (200k), leaving the page/grid
 * capture all the room it needs.
 */
export const MAX_DIAGNOSTICS_CHARS = 20_000;

/**
 * Node budget for serializing one console argument. The recorded message is
 * clipped to MAX_ERROR_MESSAGE_CHARS anyway — serializing a huge object
 * (say, a Redux-sized state tree someone console.error'd) just to throw the
 * JSON away would burn CPU on the hot console path. The replacer counts
 * visited nodes and bails to String() past the budget.
 */
const MAX_FORMAT_NODES = 256;

/** One console-ish argument as a short string (Errors keep their message). */
function formatArg(arg: unknown): string {
  if (typeof arg === "string") return arg;
  if (arg instanceof Error) return `${arg.name}: ${arg.message}`;
  try {
    let visited = 0;
    const json = JSON.stringify(arg, (_key, value: unknown) => {
      visited += 1;
      if (visited > MAX_FORMAT_NODES) {
        // Aborts the whole stringify — caught below, String() takes over.
        throw new RangeError("diagnostics format budget exceeded");
      }
      return value;
    });
    return json ?? String(arg);
  } catch {
    return String(arg);
  }
}

/** Head share of a clipped value — the rest of the budget keeps the tail. */
const CLIP_HEAD_CHARS = 300;

/** Marks where the middle of an over-budget value was cut. */
const CLIP_MARKER = " …[clipped]… ";

/**
 * Budget clip that keeps BOTH ends of an over-budget value: a head-only
 * clip of a deep (React) stack throws away its far end — often exactly the
 * frame that matters. Kept text totals `max` (head + tail) plus the marker.
 */
function clip(text: string, max: number): string {
  if (text.length <= max) return text;
  const head = Math.min(CLIP_HEAD_CHARS, max);
  const tail = max - head;
  return (
    text.slice(0, head) + CLIP_MARKER + (tail > 0 ? text.slice(-tail) : "")
  );
}

/** The first Error argument's stack, clipped — undefined when none. */
function stackOf(args: unknown[]): string | undefined {
  for (const arg of args) {
    if (arg instanceof Error && arg.stack)
      return clip(arg.stack, MAX_STACK_CHARS);
  }
  return undefined;
}

/**
 * TEMPORARY-ish: license noise filtered pending a real license key — remove
 * or keep deliberately then.
 *
 * AG Grid Enterprise without a license key prints a multi-line ASCII-box
 * banner through console.error — ONE call per box line, each asterisk-padded
 * to a fixed width by its LicenseManager (`centerPadAndOutput` /
 * `padAndOutput`): all-asterisk border lines, "* AG Grid Enterprise License *",
 * "* License Key Not Found *", "* All AG Grid Enterprise features are
 * unlocked for trial. *", "* … email info@ag-grid.com for a trial license
 * key. *". In trial mode those lines flooded the 20-slot error buffer and
 * misled a real triage. This predicate matches ONLY that banner shape: every
 * non-empty line must be either a pure asterisk border or an
 * asterisk-framed line carrying a license/trial phrase. Real AG Grid
 * diagnostics ("AG Grid: error #273 …") are not asterisk-framed and are
 * still captured. Capture-only: the observe-only wrapper has already called
 * the original console, so the banner still prints.
 */
const LICENSE_BANNER_PHRASE =
  /license|trial|info@ag-grid\.com|version you are trying to use/i;

function isAgGridLicenseBannerNoise(text: string): boolean {
  const lines = text
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  if (lines.length === 0) return false;
  return lines.every(
    (line) =>
      /^\*+$/.test(line) ||
      (line.startsWith("*") &&
        line.endsWith("*") &&
        LICENSE_BANNER_PHRASE.test(line))
  );
}

export interface DiagnosticsRecorder {
  /**
   * Wrap console.error/warn and listen for window `error` +
   * `unhandledrejection`. Idempotent (a second install is a no-op returning
   * the same uninstall) and reversible — uninstall restores the original
   * console methods (per method, only if ours is still installed — a
   * wrapper someone stacked on top of ours is left in place) and removes
   * the listeners. The wrapped console always
   * calls through to the original FIRST, so a recorder bug can never
   * swallow a log line.
   */
  install: () => () => void;
  /** The captured errors, oldest first (a copy). */
  snapshotErrors: () => ClientErrorEntry[];
  /** Test hook: reset the buffer. */
  clear: () => void;
}

export function createDiagnosticsRecorder(): DiagnosticsRecorder {
  const entries: ClientErrorEntry[] = [];
  let uninstall: (() => void) | null = null;

  function record(entry: ClientErrorEntry): void {
    entries.push(entry);
    if (entries.length > MAX_ERROR_ENTRIES) {
      entries.splice(0, entries.length - MAX_ERROR_ENTRIES);
    }
  }

  function recordConsole(
    kind: "console.error" | "console.warn",
    args: unknown[]
  ): void {
    const message = args.map(formatArg).join(" ");
    // Matched BEFORE clipping — the signature lives in the full line.
    if (isAgGridLicenseBannerNoise(message)) return;
    record({
      kind,
      message: clip(message, MAX_ERROR_MESSAGE_CHARS),
      stack: stackOf(args),
      timestamp: new Date().toISOString()
    });
  }

  function install(): () => void {
    if (uninstall) return uninstall; // already installed (StrictMode remount)

    const originalError = console.error;
    const originalWarn = console.warn;
    function wrappedError(...args: unknown[]): void {
      originalError.apply(console, args);
      try {
        recordConsole("console.error", args);
      } catch {
        // Recording must never break logging.
      }
    }
    function wrappedWarn(...args: unknown[]): void {
      originalWarn.apply(console, args);
      try {
        recordConsole("console.warn", args);
      } catch {
        // Recording must never break logging.
      }
    }
    console.error = wrappedError;
    console.warn = wrappedWarn;

    function onError(event: ErrorEvent): void {
      // Resource-load errors fire here too but carry no message — skip them.
      if (!event.message) return;
      record({
        kind: "window.onerror",
        message: clip(event.message, MAX_ERROR_MESSAGE_CHARS),
        source: event.filename
          ? `${event.filename}:${event.lineno}:${event.colno}`
          : undefined,
        stack:
          event.error instanceof Error && event.error.stack
            ? clip(event.error.stack, MAX_STACK_CHARS)
            : undefined,
        timestamp: new Date().toISOString()
      });
    }
    function onRejection(event: PromiseRejectionEvent): void {
      const reason: unknown = event.reason;
      record({
        kind: "unhandledrejection",
        message: clip(formatArg(reason), MAX_ERROR_MESSAGE_CHARS),
        stack:
          reason instanceof Error && reason.stack
            ? clip(reason.stack, MAX_STACK_CHARS)
            : undefined,
        timestamp: new Date().toISOString()
      });
    }
    window.addEventListener("error", onError);
    window.addEventListener("unhandledrejection", onRejection);

    uninstall = () => {
      // Only restore a method WE still own — if something wrapped the
      // console after us (another recorder, a test spy), reassigning the
      // original here would silently stomp that later wrapper.
      if (console.error === wrappedError) console.error = originalError;
      if (console.warn === wrappedWarn) console.warn = originalWarn;
      window.removeEventListener("error", onError);
      window.removeEventListener("unhandledrejection", onRejection);
      uninstall = null;
    };
    return uninstall;
  }

  return {
    install,
    snapshotErrors: () => entries.map((entry) => ({ ...entry })),
    clear: () => {
      entries.length = 0;
    }
  };
}

/**
 * The `context.diagnostics` block: recorded errors + the API call log,
 * truncated (oldest dropped first, largest buffer first) until the
 * serialized block fits MAX_DIAGNOSTICS_CHARS. Truncation, never rejection
 * — a report must always go through.
 */
export function snapshotDiagnostics(
  recorder: DiagnosticsRecorder
): DiagnosticsSnapshot {
  const snapshot: DiagnosticsSnapshot = {
    errors: recorder.snapshotErrors(),
    api_calls: snapshotApiCalls()
  };
  let truncated = false;
  while (
    JSON.stringify(snapshot).length > MAX_DIAGNOSTICS_CHARS &&
    (snapshot.errors.length > 0 || snapshot.api_calls.length > 0)
  ) {
    // Drop the oldest entry of whichever buffer is currently longer.
    if (snapshot.errors.length >= snapshot.api_calls.length) {
      snapshot.errors.shift();
    } else {
      snapshot.api_calls.shift();
    }
    truncated = true;
  }
  if (truncated) snapshot.truncated = true;
  return snapshot;
}
